/**
 * New-customer propositions: email → storefront login (login_hint),
 * and sticky bottom banner arm / expand behaviour.
 */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'tw_nc_sticky_';
  var KEY_ARMED = STORAGE_PREFIX + 'armed';
  var KEY_CLICKS = STORAGE_PREFIX + 'clicks';
  var KEY_EXPANDED = STORAGE_PREFIX + 'expanded';
  var KEY_FIRST_PATH = STORAGE_PREFIX + 'first_path';
  var KEY_USER_COLLAPSED = STORAGE_PREFIX + 'user_collapsed';
  var CLICK_THRESHOLD = 4;

  function yieldToMain() {
    if (globalThis.scheduler && typeof globalThis.scheduler.yield === 'function') {
      return globalThis.scheduler.yield();
    }
    return new Promise(function (resolve) {
      setTimeout(resolve, 0);
    });
  }

  function storageGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* ignore */
    }
  }

  function storageRemove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  }

  function isCustomerLoggedIn() {
    return window.customerLoggedIn === true;
  }

  function clearStickySession() {
    storageRemove(KEY_ARMED);
    storageRemove(KEY_CLICKS);
    storageRemove(KEY_EXPANDED);
    storageRemove(KEY_FIRST_PATH);
    storageRemove(KEY_USER_COLLAPSED);
  }

  function userHasCollapsed() {
    return storageGet(KEY_USER_COLLAPSED) === '1';
  }

  function dismissStickyHosts() {
    clearStickySession();
    document.querySelectorAll('[data-nc-sticky]').forEach(function (host) {
      host.hidden = true;
      host.classList.remove('is-visible', 'is-expanded');
      host.classList.add('is-collapsed');
      var section = host.closest('.shopify-section');
      if (section) section.style.display = 'none';
    });
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  function bindEmailForms(root) {
    var scope = root || document;
    var forms = scope.querySelectorAll('[data-nc-email-form]');
    forms.forEach(function (form) {
      if (form.dataset.ncBound === '1') return;
      form.dataset.ncBound = '1';

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var input = form.querySelector('input[type="email"]');
        var errorEl = form.querySelector('[data-nc-email-error]');
        var email = input ? input.value.trim() : '';
        var loginUrl = form.getAttribute('data-login-url') || '';

        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }

        if (!isValidEmail(email) || !loginUrl) {
          if (errorEl) {
            errorEl.textContent = 'Please enter a valid email address.';
            errorEl.hidden = false;
          }
          return;
        }

        var btn = form.querySelector('button[type="submit"]');
        if (btn) btn.disabled = true;

        yieldToMain().then(function () {
          var separator = loginUrl.indexOf('?') === -1 ? '?' : '&';
          window.location.assign(
            loginUrl + separator + 'login_hint=' + encodeURIComponent(email)
          );
        });
      });
    });
  }

  function initSticky(host) {
    if (!host || host.dataset.ncStickyInit === '1') return;

    // Logged-in customers never see the sticky (Liquid also gates; this covers
    // bfcache / return-from-login with a stale host still in the DOM).
    if (isCustomerLoggedIn()) {
      dismissStickyHosts();
      return;
    }

    host.dataset.ncStickyInit = '1';

    // Only arm when the visitor lands on vm-xxx-nnp / vm-xxx-npu. Overlay can
    // still host the sticky elsewhere so it can reappear after navigation once
    // armed — direct landings (e.g. homepage) stay hidden.
    var templateSuffix = host.getAttribute('data-template-suffix') || '';
    var armSuffixes = (host.getAttribute('data-arm-templates') || '')
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);

    var shouldArm =
      armSuffixes.length > 0 && armSuffixes.indexOf(templateSuffix) !== -1;

    if (shouldArm) {
      storageSet(KEY_ARMED, '1');
      if (!storageGet(KEY_FIRST_PATH)) {
        storageSet(KEY_FIRST_PATH, window.location.pathname);
      }
    }

    if (storageGet(KEY_ARMED) !== '1') {
      host.hidden = true;
      return;
    }

    host.hidden = false;
    host.classList.add('is-visible');

    var firstPath = storageGet(KEY_FIRST_PATH) || '';
    var pathChanged = firstPath !== '' && firstPath !== window.location.pathname;
    var alreadyExpanded = storageGet(KEY_EXPANDED) === '1';
    var clickCount = parseInt(storageGet(KEY_CLICKS) || '0', 10) || 0;

    function expand() {
      storageRemove(KEY_USER_COLLAPSED);
      storageSet(KEY_EXPANDED, '1');
      host.classList.add('is-expanded');
      host.classList.remove('is-collapsed');
      host.setAttribute('aria-expanded', 'true');
    }

    function collapse() {
      host.classList.remove('is-expanded');
      host.classList.add('is-collapsed');
      host.setAttribute('aria-expanded', 'false');
    }

    // Start collapsed; auto-expand only if the user has never manually collapsed.
    collapse();
    if (
      !userHasCollapsed() &&
      (alreadyExpanded || pathChanged || clickCount >= CLICK_THRESHOLD)
    ) {
      requestAnimationFrame(function () {
        expand();
      });
    }

    var toggle = host.querySelector('[data-nc-sticky-toggle]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        var willExpand = !host.classList.contains('is-expanded');
        if (willExpand) {
          expand();
        } else {
          storageSet(KEY_USER_COLLAPSED, '1');
          storageRemove(KEY_EXPANDED);
          collapse();
        }
      });
    }

    document.addEventListener(
      'click',
      function (event) {
        if (userHasCollapsed()) return;
        if (storageGet(KEY_EXPANDED) === '1') return;
        if (host.contains(event.target)) return;

        var next = (parseInt(storageGet(KEY_CLICKS) || '0', 10) || 0) + 1;
        storageSet(KEY_CLICKS, String(next));

        if (next >= CLICK_THRESHOLD) {
          host.classList.add('is-expanding');
          yieldToMain().then(function () {
            if (!userHasCollapsed()) expand();
            host.classList.remove('is-expanding');
          });
        }
      },
      true
    );

    bindEmailForms(host);
  }

  function boot() {
    if (isCustomerLoggedIn()) {
      dismissStickyHosts();
      return;
    }

    bindEmailForms(document);
    document.querySelectorAll('[data-nc-sticky]').forEach(initSticky);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Back-forward cache can restore a pre-login page with the sticky visible.
  window.addEventListener('pageshow', function () {
    if (isCustomerLoggedIn()) dismissStickyHosts();
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (isCustomerLoggedIn()) {
      dismissStickyHosts();
      return;
    }
    bindEmailForms(event.target);
    var sticky = event.target.querySelector
      ? event.target.querySelector('[data-nc-sticky]')
      : null;
    if (sticky) initSticky(sticky);
  });
})();
